import * as React from "react";
import { useEffect, useMemo } from "react";

import { useSuspenseQuery } from "@tanstack/react-query";
import { Location } from "history";
import { useLocation } from "react-router-dom";

import { postEnterOrg } from "api/generated/authentication/authentication";

import {
    clearLastChosenOrg,
    readLastChosenOrg,
} from "../utils/storage/lastChosenOrg";

const shouldRedirect = (location: Location) => {
    const urlParams = new URLSearchParams(location.search);
    const reset = urlParams.get("reset");

    if (reset !== undefined && reset !== null) {
        clearLastChosenOrg();
        return false;
    }

    return !!readLastChosenOrg();
};

const queryFn = async () => {
    const lastChosenOrg = readLastChosenOrg();
    if (!lastChosenOrg) {
        return null;
    }

    try {
        const response = await postEnterOrg({ org_short_name: lastChosenOrg });
        return response.data;
    } catch {
        clearLastChosenOrg();
        return null;
    }
};

interface AutoRedirectWrapperProps {
    children: React.ReactNode;
    redirectPath?: string;
}
export function AutoRedirectWrapper({
    children,
    redirectPath,
}: AutoRedirectWrapperProps) {
    const location = useLocation();
    const shouldAutoRedirect = useMemo(
        () => shouldRedirect(location),
        [location]
    );

    const { data: redirectTarget } = useSuspenseQuery({
        queryKey: ["autoRedirect", location.search, shouldAutoRedirect],
        queryFn: async () => (shouldAutoRedirect ? queryFn() : null),
    });

    useEffect(() => {
        if (redirectTarget?.redirect) {
            window.location.href = redirectPath
                ? new URL(redirectPath, redirectTarget.redirect).href
                : redirectTarget.redirect;
        }
    }, [redirectTarget, redirectPath]);

    return <>{children}</>;
}
